import { RecipeResponse } from 'lib/services/models/content-response';

export interface INormalizedRecipeNutritionData {
  recipeNutrition: {
    servingsPer: string | null;
    servingSize: string | null;
    calories: string | null;
    caloriesText: string;
    summaryItems: Array<{
      displayName: string;
      quantity: string;
    }>;
    nutritionItems: Array<{
      displayName: string;
      quantity: string;
      percent: string;
      rowType: string | null;
    }>;
    vitaminItems: Array<{
      displayName: string;
      quantity: string;
      percent: string;
    }>;
    exchangeItems: Array<{
      displayName: string;
      quantity: string;
    }> | null;
    carbohydrateChoice: string | null;
  };
}

export function normalizeRecipeNutritionData(
  recipe: RecipeResponse
): INormalizedRecipeNutritionData | null {
  // Make a cross reference index of nutrition items by DisplayName for easy retrieval
  const itemsIndex: {
    [key: string]: {
      DisplayValue: string;
      Unit: string;
      PercentDailyValue: string;
      TranslatedName: string;
    };
  } = {};

  recipe.NutritionItems?.forEach(
    (item: {
      DisplayName: string;
      DisplayValue: string;
      Unit: string | null;
      PercentDailyValue: string;
      TranslatedName: string;
    }) => {
      itemsIndex[item.DisplayName] = {
        DisplayValue: item.DisplayValue,
        Unit: item.Unit || '',
        PercentDailyValue: item.PercentDailyValue,
        TranslatedName: item.TranslatedName,
      };
    }
  );

  const servingNumber = recipe.ServingNumber || '';
  const servingSize = recipe.ServingSize || '';

  // Restructure nutrition items
  const normalizedData = generateRecipeNutritionItems(itemsIndex, servingNumber, servingSize);

  return normalizedData;
}

/**
 * Restructures recipe nutrition, vitamin, and exchange items
 *
 * @param {object} itemsIndex a cross reference of the nutritionItems array used for easy retrieval of array elements by DisplayName key
 * @param {string} servingNumber (can be null)
 * @param {string} servingSize (can be null)
 * @const {object} nutritionDisplay, contains nutrientItemsList, vitaminItemsList, exchangeItemsList, used to defined which items to include and their order.
 * @return {object} returns normalized recipe nutrition data including nutritionItems, vitaminItems, exchangeItems to match INutritionTableNarProps
 */
export function generateRecipeNutritionItems(
  itemsIndex: {
    [x: string]: {
      DisplayValue: string;
      Unit: string;
      PercentDailyValue: string;
      TranslatedName: string;
    };
  },
  servingNumber: string,
  servingSize: string
) {
  const summaryItems = nutritionDisplay.summaryItemsList
    .filter((listItem) => itemsIndex[listItem.displayName])
    .map((listItem) => {
      const key = listItem.displayName;
      const item = itemsIndex[key];
      if (!item) return null;

      const quantity = getValueString(item.DisplayValue, item.Unit);

      return {
        displayName: item.TranslatedName,
        quantity: quantity !== 'nc' ? quantity : '', // don't return 'nc' for summaryItems
      };
    })
    .filter((item): item is { displayName: string; quantity: string } => item !== null);

  const nutrientItems = nutritionDisplay.nutrientItemsList
    .filter((listItem) => itemsIndex[listItem.displayName])
    .map((listItem) => {
      const key = listItem.displayName;
      const item = itemsIndex[key];
      if (!item) return null;

      const quantity = getValueString(item.DisplayValue, item.Unit);
      const percent = getPercentString(item.PercentDailyValue);
      const rowType = listItem.rowType ?? null;

      return {
        displayName: item.TranslatedName,
        quantity: quantity !== 'nc' ? quantity : '', // don't return 'nc' for nutrientItems
        percent: percent,
        rowType: rowType,
      };
    })
    .filter(
      (
        item
      ): item is {
        displayName: string;
        quantity: string;
        percent: string;
        rowType: string | null;
      } => item !== null
    );

  const vitaminItems = nutritionDisplay.vitaminItemsList
    .filter((key) => itemsIndex[key])
    .map((key) => {
      const item = itemsIndex[key];
      if (!item) return null;

      const quantity = getValueString(item.DisplayValue, item.Unit);
      const percent = getPercentString(item.PercentDailyValue);

      return {
        displayName: item.TranslatedName,
        quantity: quantity !== 'nc' ? quantity : '', // don't return 'nc' for vitaminItems
        percent: percent,
      };
    })
    .filter(
      (item): item is { displayName: string; quantity: string; percent: string } => item !== null
    );

  const exchangeItems = nutritionDisplay.exchangeItemsList
    .filter((key) => itemsIndex[key])
    .map((key) => {
      const item = itemsIndex[key];
      if (!item) return null;

      return {
        displayName: item.TranslatedName,
        quantity: getValueString(item.DisplayValue, item.Unit, item.PercentDailyValue),
      };
    })
    .filter((item): item is { displayName: string; quantity: string } => item !== null);

  if (
    !nutrientItems.length &&
    !vitaminItems.length &&
    !exchangeItems.length &&
    !itemsIndex['Carbohydrate Choice']?.DisplayValue
  )
    return null;

  const data = {
    recipeNutrition: {
      servingsPer: stringToFraction(servingNumber, true),
      servingSize: stringToFraction(servingSize, true),
      calories: itemsIndex['Calories']?.DisplayValue || null,
      caloriesText: itemsIndex['Calories']?.TranslatedName || 'Calories',
      summaryItems: summaryItems,
      nutritionItems: nutrientItems,
      vitaminItems: vitaminItems,
      exchangeItems: exchangeItems.length ? exchangeItems : null,
      carbohydrateChoice: stringToFraction(
        itemsIndex['Carbohydrate Choice']?.DisplayValue || null,
        true
      ),
    },
  };

  return data;
}

// Trim trailing zeros from numbers
function getPercentString(percent: string) {
  return percent?.length ? Number(Number(percent).toFixed(4)) + '%' : '';
}

// Parse values of a nutrition item into a readable string
function getValueString(displayValue: string, unit: string, percentDailyValue: string = '') {
  let valueString = '';
  if ('nc' === displayValue.toLowerCase()) {
    valueString = 'nc';
  } else {
    const valueHtml = stringToFraction(displayValue, true);
    const unitText = unit ? unit : '';
    const displayText = valueHtml && '%' !== unit ? valueHtml + unitText : '';
    const percentText = getPercentString(percentDailyValue);
    valueString = displayText + percentText;
  }

  return valueString;
}

/**
 * Replaces text fractions with proper HTML entities and optional hidden screen reader text.
 * Example: 2 1/2 would normally read 'two one slash two', now displays as a vulgar fraction and reads 'two and one half'
 *
 * @param {string} stringFraction a character string representing a fractional value like 2 1/2
 * @param {boolean} html (optional) true adds screen reader text, false returns only the converted string
 * @return {string} numeric fraction converted to a corresponding HTML entity, with optional ampersand hidden screen reader text
 */
function stringToFraction(stringFraction: string | null, html?: boolean) {
  if (!stringFraction) return stringFraction;

  // List of fractions to convert to vulgar fraction HTML entities
  const fractions = [
    ['1/8', '&frac18;'],
    ['1/4', '&frac14;'],
    ['3/8', '&frac38;'],
    ['1/2', '&frac12;'],
    ['5/8', '&frac58;'],
    ['3/4', '&frac34;'],
    ['7/8', '&frac78;'],
    ['1/3', '&frac13;'],
    ['2/3', '&frac23;'],
  ];

  // Regex for any digit followed by a fraction (ex: 2 1/2 reads "two and one half")
  let fraction = html
    ? stringFraction.replace(/(\d) (\d\/\d)/, '$1<span class="screenReaderText">&amp;</span>$2')
    : stringFraction;

  // Replace some basic fractions with HTML entity
  fractions.find((i) => {
    fraction = fraction.replace(i[0], i[1]);
  });

  return fraction;
}

// Nutrition table display order.
const nutritionDisplay = {
  summaryItemsList: [
    { displayName: 'Calories' },
    { displayName: 'Total Fat' },
    { displayName: 'Protein' },
    { displayName: 'Total Carbohydrate' },
    { displayName: 'Sugars' },
  ],
  nutrientItemsList: [
    { displayName: 'Total Fat' },
    { displayName: 'Saturated Fat', rowType: 'subitem' },
    { displayName: 'Trans Fat', rowType: 'subitem' },
    { displayName: 'Monounsaturated Fat', rowType: 'subitem' },
    { displayName: 'Polyunsaturated Fat', rowType: 'subitem' },
    { displayName: 'Omega-3' },
    { displayName: 'Cholesterol' },
    { displayName: 'Sodium' },
    { displayName: 'Total Carbohydrate' },
    { displayName: 'Dietary Fiber', rowType: 'subitem' },
    { displayName: 'Soluble Fiber', rowType: 'subitem' },
    { displayName: 'Insoluble Fiber', rowType: 'subitem' },
    { displayName: 'Sugars', rowType: 'subitem' },
    { displayName: 'Added Sugars', rowType: 'subitem' },
    { displayName: 'Protein' },
  ],
  vitaminItemsList: [
    'Vitamin D',
    'Calcium',
    'Iron',
    'Potassium',
    'Vitamin A',
    'Vitamin C',
    'Vitamin E',
    'Thiamin',
    'Riboflavin',
    'Niacin',
    'Vitamin B6',
    'Folic Acid',
    'Vitamin B12',
    'Pantothenic Acid',
    'Phosphorus',
    'Magnesium',
    'Zinc',
    'Manganese',
    'Selenium',
    'Copper',
    'Beta-Carotene',
  ],
  exchangeItemsList: [
    'Starch',
    'Fruit',
    'Other Carbohydrate',
    'Skim Milk',
    'Low-Fat Milk',
    'Milk',
    'Vegetable',
    'Very Lean Meat',
    'Lean Meat',
    'Medium-Fat Meat',
    'High-Fat Meat',
    'Fat',
    'Fruits',
    'Vegetables',
    'Grains',
    'Meat & Beans',
    'Dairy',
    'Fats & Oils',
  ],
};
